import React, { useState } from 'react';
import { GroupMemberProfile } from '../types';
import { Trophy, Award, Target, Shield, Users, Star, Flame, Filter } from 'lucide-react';

interface LeaderboardViewProps {
  members: GroupMemberProfile[];
  onSelectMember?: (member: GroupMemberProfile) => void;
}

export const LeaderboardView: React.FC<LeaderboardViewProps> = ({ members, onSelectMember }) => {
  const [activeCategory, setActiveCategory] = useState<
    'ratings' | 'goals' | 'assists' | 'contributions' | 'motm' | 'cleansheets' | 'appearances' | 'winrate'
  >('goals');

  const [selectedSeason, setSelectedSeason] = useState<string>('all_time');
  const [minMatchesFilter, setMinMatchesFilter] = useState<boolean>(true);

  const activeMembers = members.filter((m) => m.isActive);

  const getFilteredAndSortedMembers = () => {
    let list = [...activeMembers];

    // Filter by minimum matches threshold for rate-based metrics
    if (minMatchesFilter && (activeCategory === 'ratings' || activeCategory === 'winrate')) {
      list = list.filter((m) => m.stats.matchesPlayed >= 3);
    }

    switch (activeCategory) {
      case 'goals':
        return list.sort((a, b) => b.stats.goals - a.stats.goals);
      case 'assists':
        return list.sort((a, b) => b.stats.assists - a.stats.assists);
      case 'contributions':
        return list.sort((a, b) => (b.stats.goals + b.stats.assists) - (a.stats.goals + a.stats.assists));
      case 'motm':
        return list.sort((a, b) => b.stats.motmCount - a.stats.motmCount);
      case 'cleansheets':
        return list.sort((a, b) => b.stats.cleanSheets - a.stats.cleanSheets);
      case 'appearances':
        return list.sort((a, b) => b.stats.matchesPlayed - a.stats.matchesPlayed);
      case 'winrate':
        return list.sort((a, b) => b.stats.winPct - a.stats.winPct);
      case 'ratings':
      default:
        return list.sort((a, b) => b.footballCard.overall - a.footballCard.overall);
    }
  };

  const sortedList = getFilteredAndSortedMembers();

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-20 text-white">
      {/* Top Banner */}
      <div className="bg-[#121614] border border-white/5 p-5 rounded-2xl flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white">Group Leaderboards</h2>
          <p className="text-xs text-slate-400 font-medium">
            Top performers across goals, assists, ratings, MOTM awards, and win rates
          </p>
        </div>

        {/* Season & Min Matches Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(e.target.value)}
            className="bg-[#171c19] text-white border border-white/10 text-xs font-bold rounded-xl px-3 py-1.5 focus:outline-none"
          >
            <option value="all_time">All Time</option>
            <option value="2026">2026 Season</option>
            <option value="2025">2025 Season</option>
          </select>

          <button
            onClick={() => setMinMatchesFilter(!minMatchesFilter)}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition ${
              minMatchesFilter
                ? 'bg-[#1c221d] text-[#a8c936] border-[#a8c936]/30'
                : 'bg-[#171c19] text-slate-400 border-white/5'
            }`}
          >
            Min 3 Matches
          </button>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex border-b border-white/5 bg-[#121614] p-1.5 rounded-2xl overflow-x-auto gap-1 text-xs font-semibold">
        {[
          { id: 'goals', label: '⚽ Top Scorers' },
          { id: 'assists', label: '🎯 Assists' },
          { id: 'contributions', label: '🔥 Goal Contrib' },
          { id: 'motm', label: '⭐ MOTM Awards' },
          { id: 'ratings', label: '🏅 Highest OVR' },
          { id: 'cleansheets', label: '🛡️ Clean Sheets' },
          { id: 'appearances', label: '👕 Matches' },
          { id: 'winrate', label: '📊 Win Rate %' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveCategory(tab.id as any)}
            className={`py-2 px-3.5 rounded-xl whitespace-nowrap transition ${
              activeCategory === tab.id
                ? 'bg-[#1c221d] text-[#a8c936] font-bold border border-white/5'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Leaderboard Table List */}
      <div className="bg-[#121614] border border-white/5 rounded-2xl overflow-hidden shadow-lg">
        <div className="divide-y divide-white/5">
          {sortedList.length === 0 ? (
            <div className="p-8 text-center text-xs text-slate-400 font-medium">
              No players meet the minimum match threshold for this leaderboard.
            </div>
          ) : (
            sortedList.map((mem, index) => {
              let rankBadge = `${index + 1}`;
              let bgStyle = 'hover:bg-white/5';

              if (index === 0) {
                rankBadge = '🥇';
                bgStyle = 'bg-[#1c221d] border-l-2 border-[#a8c936]';
              } else if (index === 1) {
                rankBadge = '🥈';
              } else if (index === 2) {
                rankBadge = '🥉';
              }

              let statVal = `${mem.stats.goals} Goals`;
              if (activeCategory === 'assists') statVal = `${mem.stats.assists} Assists`;
              if (activeCategory === 'contributions') statVal = `${mem.stats.goals + mem.stats.assists} G+A`;
              if (activeCategory === 'motm') statVal = `${mem.stats.motmCount} MOTM`;
              if (activeCategory === 'cleansheets') statVal = `${mem.stats.cleanSheets} Clean Sheets`;
              if (activeCategory === 'appearances') statVal = `${mem.stats.matchesPlayed} Matches`;
              if (activeCategory === 'winrate') statVal = `${mem.stats.winPct}%`;
              if (activeCategory === 'ratings') statVal = `${mem.footballCard.overall} OVR`;

              return (
                <div
                  key={mem.id}
                  onClick={() => onSelectMember && onSelectMember(mem)}
                  className={`p-3.5 flex items-center justify-between transition cursor-pointer ${bgStyle}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-6 text-center font-mono font-bold text-xs text-slate-400">{rankBadge}</span>
                    <img
                      src={mem.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100'}
                      alt={mem.displayName}
                      className="w-10 h-10 rounded-xl object-cover border border-white/10"
                    />
                    <div>
                      <span className="font-semibold text-white text-sm block">{mem.displayName}</span>
                      <span className="text-[11px] text-slate-400 font-mono">
                        {mem.primaryPosition} • #{mem.jerseyNumber}
                      </span>
                    </div>
                  </div>

                  <div className="text-right">
                    <span className="font-extrabold text-sm font-mono text-[#a8c936] block">{statVal}</span>
                    <span className="text-[10px] text-slate-400 font-mono">{mem.stats.matchesPlayed} Matches</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
